// eslint-disable-next-line no-use-before-define
import React, { useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { getLoginByCorporateId } from 'modules/auth/utils/getLoginByCorporateId';
import { useCustomAuth } from '../../contexts/CustomAuthContext';

const OTP_CODE_DIGITS = Number(process.env.REACT_APP_OTP_CODE_DIGITS) || 8;

interface CustomLoginFormProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const CustomLoginForm: React.FC<CustomLoginFormProps> = ({
  onSuccess,
  onError,
}) => {
  const { t } = useTranslate();
  const {
    login,
    sendOtp,
    isLoading,
    error,
    clearError,
    session,
    rateLimitInfo,
    clearRateLimitInfo,
  } = useCustomAuth();

  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [formError, setFormError] = useState('');

  const { button } = getLoginByCorporateId();

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();

    clearError();
    setFormError('');
    clearRateLimitInfo();

    if (!email) {
      setFormError(t('LoginErrorEmailEmpty'));
      return;
    }

    if (!email.includes('@')) {
      setFormError(t('LoginErrorEmailInvalid'));
      return;
    }

    try {
      await sendOtp({ email });
      setOtpSent(true);
      setFormError('');
    } catch (error: any) {
      const errorMessage = error.message || t('LoginErrorOTPFailed');
      setFormError(errorMessage);
      onError?.(errorMessage);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    clearError();
    setFormError('');
    clearRateLimitInfo();

    // Basic validation
    if (!email || !otp) {
      setFormError('LoginErrorFillAllFields');
      return;
    }

    if (!email.includes('@')) {
      setFormError('LoginErrorEmailInvalid');
      return;
    }

    if (otp.length !== OTP_CODE_DIGITS) {
      setFormError('LoginErrorOTPEnterTheCode');
      return;
    }

    if (!session) {
      setFormError('LoginErrorSessionExpired');
      return;
    }

    try {
      await login({ email, otp, session: session || '' });
      setFormError('');
      onSuccess?.();
    } catch (error: any) {
      const errorMessage = error.message || t('LoginErrorOTPInvalid');
      setFormError(errorMessage);
      onError?.(errorMessage);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (formError) setFormError('');
    if (error) clearError();
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, OTP_CODE_DIGITS);
    setOtp(value);
    if (formError) setFormError('');
    if (error) clearError();
  };

  const handleResendOtp = async () => {
    try {
      await sendOtp({ email });
      setFormError('');
    } catch (error: any) {
      const errorMessage = error.message || 'LoginErrorOTPResend';
      setFormError(errorMessage);
    }
  };

  const displayError = formError || error;

  return (
    <div className="auth-form">
      {!otpSent ? (
        <form onSubmit={handleSendOtp} className="form-validate">
          <div className="form-group mt-3">
            <div className="form-control-wrap">
              <input
                type="email"
                className="form-control form-control-lg"
                id="email"
                placeholder={t('LoginEmailLabelPlaceholder')}
                value={email}
                onChange={handleEmailChange}
                disabled={isLoading}
                required
              />
            </div>
          </div>

          {displayError && (
            <div className="alert alert-danger">
              <div className="alert-text">{t(displayError)}</div>
            </div>
          )}

          {/* Rate limiting info */}
          {email && rateLimitInfo && !rateLimitInfo.isBlocked && (
            <div className="alert alert-warning">
              <div className="alert-text">
                {rateLimitInfo.remainingAttempts === 0
                  ? 'No attempts remaining. Please wait 15 minutes before trying again.'
                  : `${rateLimitInfo.remainingAttempts} attempt${
                      rateLimitInfo.remainingAttempts === 1 ? '' : 's'
                    } remaining.`}
              </div>
            </div>
          )}

          {rateLimitInfo && rateLimitInfo.isBlocked && (
            <div className="alert alert-danger">
              <div className="alert-text">
                {t('TooManyFailedAttemptsPleaseTryAgainLater')}
              </div>
            </div>
          )}

          <div className="form-group">
            <button
              type="submit"
              className="btn btn-lg btn-primary btn-block btn-login"
              disabled={isLoading || rateLimitInfo?.isBlocked}
            >
              {isLoading ? (
                <>
                  <em className="icon ni ni-loader animated-icon-rotation" />
                  <span>{t('LoginRequestCodeLoading')}</span>
                </>
              ) : (
                <>
                  <em className={`icon ni ni-send ${button.icon}`} />
                  <span>{t(button.text)}</span>
                </>
              )}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleLogin}>
          <div className="form-group mt-3">
            {/* <div className="form-label-group">
              <label className="form-label" htmlFor="otp">
                {t('LoginVerificationCodeLabel')}
              </label>
            </div> */}
            <div className="form-control-wrap">
              <input
                type="text"
                className="form-control form-control-lg text-center"
                id="otp"
                placeholder={t('LoginVerificationCodeLabelPlaceholder')}
                value={otp}
                onChange={handleOtpChange}
                disabled={isLoading}
                maxLength={OTP_CODE_DIGITS}
                required
                style={{ letterSpacing: '0.5em', fontSize: '1.2em' }}
              />
            </div>
            <div className="form-note-s2 text-left pt-2">
              <small>{t('LoginVerificationCodeDescription')}</small>
            </div>
          </div>

          {displayError && rateLimitInfo?.remainingAttempts !== 0 && (
            <div className="alert alert-danger">
              <div className="alert-text">{t(displayError)}</div>
            </div>
          )}

          {/* Rate limiting info for OTP form */}
          {rateLimitInfo && (
            <div className="alert alert-warning">
              <div className="alert-text">
                {rateLimitInfo.remainingAttempts === 0
                  ? t('LOGIN_ERROR_TOO_MANY_ATTEMPTS_PLEASE_TRY_AGAIN_LATER')
                  : `${rateLimitInfo.remainingAttempts} ${
                      rateLimitInfo.remainingAttempts === 1
                        ? t('attempt')
                        : t('attempts')
                    } ${t('remaining')}.`}
              </div>
            </div>
          )}

          {rateLimitInfo?.remainingAttempts !== 0 && (
            <>
              <div className="form-group">
                <button
                  type="submit"
                  className="btn btn-lg btn-primary btn-block btn-login"
                  disabled={isLoading || rateLimitInfo?.remainingAttempts === 0}
                >
                  {isLoading ? (
                    <>
                      <em className="icon ni ni-loader animated-icon-rotation" />
                      <span>{t('LoginVerifying')}</span>
                    </>
                  ) : (
                    <>
                      <em className="icon ni ni-check" />
                      <span>{t('LoginVerifyAndSignIn')}</span>
                    </>
                  )}
                </button>
              </div>

              <div className="form-note-s2 text-center pt-4">
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={handleResendOtp}
                  disabled={isLoading || rateLimitInfo?.remainingAttempts === 0}
                >
                  {t('LoginResendCode')}
                </button>
                |
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={() => {
                    setOtpSent(false);
                    setOtp('');
                    setFormError('');
                    clearError();
                  }}
                  disabled={isLoading || rateLimitInfo?.remainingAttempts === 0}
                >
                  {t('LoginUseDifferentEmail')}
                </button>
              </div>
            </>
          )}
        </form>
      )}
    </div>
  );
};

export default CustomLoginForm;
